import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo, Wordmark } from "../components/Logo";
import {
  ArrowRight, Wand2, BookOpen, MessagesSquare, Sparkles,
  Timer, ScreenShareOff, User2,
  Flame, Frown, Focus, Brain, Volume2, ShieldAlert,
  Heart, ShieldCheck, HandHeart, Award, Sunrise, Handshake,
  Globe,
} from "lucide-react";

// ============================================================================
// BILINGUAL COPY. English / हिन्दी
// ============================================================================
const COPY = {
  en: {
    langLabel: "हिन्दी",
    signIn: "Sign in",
    hero: {
      badge: "NEUROEQ · AGES 4–13",
      headline1: "Raise Disciplined Children.",
      headline2: "Build Stronger Bonds.",
      sub: "Help your child develop self-discipline, focus, responsibility and emotional control — not through constant reminders, but through meaningful time together.",
      subLine2: "30 minutes. Every Sunday. One step at a time.",
      cta: "Start Your 2 Free Sundays",
      ctaSub: "No credit card · Free trial",
      imageAlt: "An Indian mother, father and daughter sitting together at a warmly-lit table, reading a storybook and smiling",
      problemStripLabel: "Discipline struggles we help with…",
      problemChips: ["Not listening", "Short attention", "Giving up quickly", "Phone overuse", "Angry outbursts", "Avoiding tasks"],
    },
    usps: [
      { icon: User2,          title: "Personalized",  sub: "for your child" },
      { icon: Timer,          title: "Only 30 min",  sub: "once a week" },
      { icon: ScreenShareOff, title: "Screen-free",  sub: "family time" },
      { icon: Globe,          title: "No language barrier", sub: "हिंदी & English" },
    ],
    hook: {
      eyebrow: "Not another parenting app",
      title: "Tired of reminding your child again and again?",
      body: "Every parent has said it a hundred times: \"How many times do I have to tell you?\" The answer isn't more instructions. It's more meaningful connection, guided practice and consistent habits — the way children have always learned.",
      kicker: "Not more instructions. More meaningful connection.",
    },
    pillars: {
      eyebrow: "Three pillars",
      title: "How NeuroEQ builds self-discipline",
      items: [
        { pillar: "Connect",
          title: "Strengthen the parent-child bond",
          body: "Short stories, honest listening and guided conversations that bring your child closer, not further away." },
        { pillar: "Practise",
          title: "Practise the real-life skills",
          body: "Weekly activities and small challenges that let your child practise focus, patience, responsibility and emotional regulation — not just talk about them." },
        { pillar: "Grow",
          title: "Grow into ownership",
          body: "Children gradually take more ownership of their routines, choices and feelings — one Sunday at a time." },
      ],
    },
    problems: {
      eyebrow: "Sound familiar?",
      title: "The discipline moments every parent knows.",
      sub: "You're not doing anything wrong — these moments are universal. NeuroEQ turns each one into one guided Sunday conversation.",
      items: [
        { icon: Flame,          title: "Angry outbursts",
          moment: "He doesn't know where the frustration goes, so it comes out as a shout.",
          fix: "Sunday's story gives feelings a name, so he can pause before the shout." },
        { icon: ScreenShareOff, title: "Phone overuse",
          moment: "The phone is easier than sitting with a feeling she can't explain.",
          fix: "30 screen-free minutes kids actually ask for — practice at choosing something harder." },
        { icon: Frown,          title: "Giving up quickly",
          moment: "\"I can't\" isn't laziness — it's a child who's decided not to risk trying.",
          fix: "A story where your child is the hero of their own effort, then a talk that says: finishing matters." },
        { icon: Focus,          title: "Short attention",
          moment: "His mind is already three thoughts ahead of the one in front of him.",
          fix: "One story, one talk, fully — 30 minutes of practice at staying with one thing." },
        { icon: Volume2,        title: "Not listening",
          moment: "\"Fine\" is easier to say than finding the words for what actually happened.",
          fix: "Questions built to open a child up, not interrogate — the kind that get a real answer." },
        { icon: ShieldAlert,    title: "Avoiding tasks",
          moment: "The task gets pushed, the room stays messy, the deadline sneaks up.",
          fix: "A weekly challenge that lets your child own one small thing, and feel the pride that follows." },
      ],
    },
    how: {
      eyebrow: "The Sunday ritual",
      title: "Four small moments. One evening. Every week.",
      sub: "No lecture, no worksheet — a lit path from a story to a real conversation, with one discipline goal every week.",
      stepLabel: "Step",
      steps: [
        { icon: Wand2,           title: "AI personalizes",       sub: "Shaped around your child's name, age and the one habit you're building this week." },
        { icon: BookOpen,        title: "A short story",         sub: "Drawn from the Ramayana, Panchatantra and more — retold for their age." },
        { icon: MessagesSquare,  title: "Parent–child talk",     sub: "Guided questions that help your child open up, instead of shutting down." },
        { icon: Sparkles,        title: "Activity & challenge",  sub: "One activity to practise this week — and one small challenge to carry into Monday." },
      ],
    },
    outcomes: {
      title: "What your child develops",
      sub: "The quiet skills behind self-discipline and independence.",
      items: [
        { icon: ShieldCheck, label: "Emotional control" },
        { icon: Focus,        label: "Focus" },
        { icon: Handshake,    label: "Responsibility" },
        { icon: Sunrise,      label: "Resilience" },
        { icon: Award,        label: "Confidence" },
        { icon: HandHeart,    label: "Empathy" },
      ],
    },
    unique: {
      badge: "Uniquely NeuroEQ",
      title: "Timeless Indian wisdom, one Sunday story at a time.",
      body: "NeuroEQ introduces children to the timeless wisdom of the Ramayana, Mahabharata, Bhagavad Gita and other great Indian stories — retold in age-appropriate language and paired with guided family conversations that turn values into weekly practice.",
      chips: ["Ramayana", "Mahabharata", "Bhagavad Gita", "Panchatantra", "Jataka Tales"],
    },
    finalCta: {
      title: "Discipline isn't taught. It's practised — together.",
      cta: "Start Your 2 Free Sundays",
      note: "30 minutes a Sunday · No ads, no data selling",
    },
    footer: "Made in India · For families everywhere",
  },
  hi: {
    langLabel: "English",
    signIn: "साइन इन",
    hero: {
      badge: "NeuroEQ · उम्र 4–13",
      headline1: "अनुशासित बच्चे।",
      headline2: "मज़बूत रिश्ते।",
      sub: "बार-बार टोकने से नहीं — साथ बिताए हुए सार्थक समय से। अपने बच्चे में आत्म-अनुशासन, एकाग्रता, ज़िम्मेदारी और भावनात्मक नियंत्रण विकसित करें।",
      subLine2: "30 मिनट. हर रविवार. एक समय में एक क़दम।",
      cta: "अपने 2 मुफ़्त रविवार शुरू करें",
      ctaSub: "क्रेडिट कार्ड नहीं · मुफ़्त ट्रायल",
      imageAlt: "एक भारतीय माँ, पिता और बेटी गर्म रौशनी वाली मेज़ पर एक साथ बैठकर मुस्कुराते हुए कहानी की किताब पढ़ रहे हैं",
      problemStripLabel: "ये अनुशासन-संबंधी बातें हम हल करते हैं…",
      problemChips: ["बात न मानना", "कम ध्यान", "जल्दी हार मानना", "फ़ोन की अति", "ग़ुस्सा", "काम टालना"],
    },
    usps: [
      { icon: User2,          title: "व्यक्तिगत",     sub: "आपके बच्चे के लिए" },
      { icon: Timer,          title: "केवल 30 मिनट",  sub: "हफ़्ते में एक बार" },
      { icon: ScreenShareOff, title: "स्क्रीन-मुक्त", sub: "पारिवारिक समय" },
      { icon: Globe,          title: "भाषा की बाधा नहीं", sub: "हिंदी और English" },
    ],
    hook: {
      eyebrow: "यह कोई और पैरेंटिंग ऐप नहीं है",
      title: "क्या बार-बार टोकते-टोकते थक गए हैं?",
      body: "हर माता-पिता ने सौ बार कहा होगा: \"कितनी बार बताना पड़ेगा?\" जवाब और निर्देशों में नहीं है — बल्कि गहरे जुड़ाव, मार्गदर्शित अभ्यास और लगातार आदतों में है। यही तरीक़ा है जिससे बच्चे हमेशा सीखते आए हैं।",
      kicker: "ज़्यादा निर्देश नहीं। गहरा जुड़ाव।",
    },
    pillars: {
      eyebrow: "तीन स्तंभ",
      title: "NeuroEQ कैसे आत्म-अनुशासन बनाता है",
      items: [
        { pillar: "जुड़ाव",
          title: "माता-पिता–बच्चे के रिश्ते को मज़बूत करें",
          body: "छोटी कहानियाँ, सच्ची सुनवाई और मार्गदर्शित बातचीत — जो बच्चे को आपसे दूर नहीं, क़रीब लाती है।" },
        { pillar: "अभ्यास",
          title: "असली ज़िंदगी के कौशल का अभ्यास",
          body: "हर हफ़्ते की गतिविधियाँ और छोटे चैलेंज — बच्चे को एकाग्रता, धैर्य, ज़िम्मेदारी और भावनात्मक नियंत्रण का असली अभ्यास देते हैं।" },
        { pillar: "विकास",
          title: "ज़िम्मेदारी की ओर बढ़ना",
          body: "बच्चे धीरे-धीरे अपनी दिनचर्या, निर्णय और भावनाओं पर ख़ुद ज़िम्मेदारी लेते हैं — एक रविवार में एक क़दम।" },
      ],
    },
    problems: {
      eyebrow: "जाना-पहचाना लगता है?",
      title: "अनुशासन के वो पल जो हर माता-पिता जानते हैं।",
      sub: "आप कुछ ग़लत नहीं कर रहे — ये पल सबके जीवन में आते हैं। NeuroEQ इन्हें हर रविवार की एक मार्गदर्शित बातचीत में बदल देता है।",
      items: [
        { icon: Flame,          title: "ग़ुस्से का विस्फोट",
          moment: "उसे नहीं पता चिड़चिड़ाहट कहाँ जाए — इसलिए वो चीख़ बनकर बाहर आती है।",
          fix: "रविवार की कहानी भावना को नाम देती है, चीख़ बनने से पहले रुकना सिखाती है।" },
        { icon: ScreenShareOff, title: "फ़ोन की अति",
          moment: "फ़ोन आसान लगता है — उस भावना के साथ बैठने से, जिसे वो समझा नहीं पाती।",
          fix: "30 स्क्रीन-मुक्त मिनट, जो बच्चा ख़ुद माँगेगा — कुछ कठिन चुनने का अभ्यास।" },
        { icon: Frown,          title: "जल्दी हार मानना",
          moment: "\"मुझसे नहीं होगा\" — आलस नहीं है, ये बच्चा है जिसने कोशिश का जोखिम न लेने का फ़ैसला कर लिया।",
          fix: "कहानी जहाँ आपका बच्चा अपनी मेहनत का हीरो है, फिर बातचीत जो कहती है: पूरा करना मायने रखता है।" },
        { icon: Focus,          title: "कम ध्यान",
          moment: "उसका मन सामने वाली बात से तीन ख़याल आगे निकल चुका है।",
          fix: "एक कहानी, एक बातचीत, पूरी तरह — एक चीज़ पर टिके रहने के 30 मिनट।" },
        { icon: Volume2,        title: "बात न मानना",
          moment: "\"ठीक\" कहना आसान है — बजाय उन शब्दों को खोजने के जो सच में हुआ था।",
          fix: "ऐसे प्रश्न जो बच्चे को खोलें, पूछताछ नहीं करें — जिनके एक-शब्द से ज़्यादा जवाब मिलें।" },
        { icon: ShieldAlert,    title: "काम टालना",
          moment: "काम आगे खिसकता है, कमरा गंदा रहता है, डेडलाइन चुपके से आ जाती है।",
          fix: "एक साप्ताहिक चैलेंज जो बच्चे को एक छोटी ज़िम्मेदारी ख़ुद उठाने देता है — और उसका गर्व महसूस करने देता है।" },
      ],
    },
    how: {
      eyebrow: "रविवार का सत्र",
      title: "चार छोटे पल. एक शाम. हर हफ़्ते।",
      sub: "कोई भाषण नहीं, कोई वर्कशीट नहीं — बस एक कहानी से असली बातचीत तक का रौशन रास्ता, हर हफ़्ते एक अनुशासन लक्ष्य।",
      stepLabel: "चरण",
      steps: [
        { icon: Wand2,          title: "AI व्यक्तिगत बनाता है", sub: "आपके बच्चे का नाम, उम्र और इस हफ़्ते की आदत के अनुसार।" },
        { icon: BookOpen,       title: "एक छोटी कहानी",         sub: "रामायण, पंचतंत्र और अन्य — बच्चे की उम्र के अनुसार सुनाई गई।" },
        { icon: MessagesSquare, title: "माता-पिता व बच्चे की बात", sub: "प्रश्न जो बच्चे को खुलने में मदद करते हैं, चुप नहीं कराते।" },
        { icon: Sparkles,       title: "गतिविधि व चैलेंज",      sub: "एक अभ्यास इस हफ़्ते — और एक छोटा चैलेंज सोमवार तक के लिए।" },
      ],
    },
    outcomes: {
      title: "आपका बच्चा क्या विकसित करता है",
      sub: "आत्म-अनुशासन और स्वतंत्रता के पीछे के ख़ामोश कौशल।",
      items: [
        { icon: ShieldCheck, label: "भावनात्मक नियंत्रण" },
        { icon: Focus,        label: "एकाग्रता" },
        { icon: Handshake,    label: "ज़िम्मेदारी" },
        { icon: Sunrise,      label: "लचीलापन" },
        { icon: Award,        label: "आत्मविश्वास" },
        { icon: HandHeart,    label: "सहानुभूति" },
      ],
    },
    unique: {
      badge: "NeuroEQ की विशेषता",
      title: "अमर भारतीय ज्ञान. एक रविवार, एक कहानी।",
      body: "NeuroEQ बच्चों को रामायण, महाभारत, भगवद् गीता और अन्य महान भारतीय कथाओं की अमर बुद्धिमत्ता से परिचित कराता है — उम्र के अनुरूप कहानियों और परिवार की संवाद के ज़रिए जो मूल्यों को साप्ताहिक अभ्यास में बदल देती हैं।",
      chips: ["रामायण", "महाभारत", "भगवद् गीता", "पंचतंत्र", "जातक कथाएँ"],
    },
    finalCta: {
      title: "अनुशासन सिखाया नहीं जाता. वो साथ अभ्यास से बनता है।",
      cta: "अपने 2 मुफ़्त रविवार शुरू करें",
      note: "30 मिनट प्रति रविवार · कोई विज्ञापन नहीं, कोई डेटा बिक्री नहीं",
    },
    footer: "भारत में बना · हर परिवार के लिए",
  },
};

// ============================================================================
// PRESENTATIONAL PIECES
// ============================================================================
function Section({ children, className = "" }) {
  return (
    <section className={`max-w-5xl mx-auto px-6 sm:px-8 ${className}`}>{children}</section>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-black/5 text-sm text-[hsl(var(--neu-ink))]">
      {children}
    </span>
  );
}

// First-visit language picker. Shown once, then remembered in localStorage.
function LanguagePickerModal({ onPick }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog" aria-modal="true"
          data-testid="splash-lang-modal">
      <div className="absolute inset-0"
           style={{ background: "rgba(23,21,49,0.72)", backdropFilter: "blur(6px)" }} />
      <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl shadow-[#171531]/30 p-7 sm:p-8"
           style={{ animation: "fadeIn 300ms ease-out" }}>
        <div className="flex items-center justify-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-[hsl(var(--neu-orange))]" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Language · भाषा
          </span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-center leading-tight">
          Choose your language
        </h2>
        <p className="font-display text-lg sm:text-xl text-center leading-tight mt-1"
           style={{ color: "hsl(var(--neu-orange))" }}>
          अपनी भाषा चुनें
        </p>
        <p className="text-sm text-muted-foreground text-center mt-4">
          You can change this anytime from the top-right corner.<br/>
          <span lang="hi">आप इसे ऊपर-दायें कोने से कभी भी बदल सकते हैं।</span>
        </p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <button
            onClick={() => onPick("en")}
            data-testid="splash-lang-modal-en"
            className="rounded-2xl border-2 border-[hsl(var(--neu-orange))]/25 bg-white hover:border-[hsl(var(--neu-orange))] hover:bg-[hsl(var(--neu-orange))]/5 transition-all p-4 text-left group"
          >
            <div className="font-display text-2xl text-[hsl(var(--neu-ink))]">English</div>
            <div className="text-xs text-muted-foreground mt-1">
              Continue in English
            </div>
            <div className="text-xs mt-3 font-medium text-[hsl(var(--neu-orange))] inline-flex items-center gap-1">
              Select <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform"/>
            </div>
          </button>
          <button
            onClick={() => onPick("hi")}
            data-testid="splash-lang-modal-hi"
            className="rounded-2xl border-2 border-[hsl(var(--neu-orange))]/25 bg-white hover:border-[hsl(var(--neu-orange))] hover:bg-[hsl(var(--neu-orange))]/5 transition-all p-4 text-left group"
            lang="hi"
          >
            <div className="font-display text-2xl text-[hsl(var(--neu-ink))]">हिन्दी</div>
            <div className="text-xs text-muted-foreground mt-1">
              हिन्दी में जारी रखें
            </div>
            <div className="text-xs mt-3 font-medium text-[hsl(var(--neu-orange))] inline-flex items-center gap-1">
              चुनें <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform"/>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function Splash() {
  // Detect whether the visitor has ever explicitly picked a language.
  const storedLang = typeof window !== "undefined" ? localStorage.getItem("neuroeq_lang") : null;
  const [lang, setLang] = useState(() => storedLang || "en");
  const [pickerOpen, setPickerOpen] = useState(() => !storedLang);
  useEffect(() => {
    // Only persist AFTER the visitor has explicitly chosen.
    if (!pickerOpen) localStorage.setItem("neuroeq_lang", lang);
  }, [lang, pickerOpen]);
  // Prevent body scroll while the language picker is up.
  useEffect(() => {
    if (!pickerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [pickerOpen]);
  const pickLang = (choice) => {
    setLang(choice);
    setPickerOpen(false);
  };
  const t = COPY[lang];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="splash-page" lang={lang}>
      {pickerOpen && <LanguagePickerModal onPick={pickLang} />}
      {/* ─── Header ─────────────────────────────────────────── */}
      <header className="max-w-5xl mx-auto px-6 sm:px-8 pt-6 flex items-center justify-between fade-in">
        <Logo size={112} />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-black/10 hover:bg-white transition-colors"
            data-testid="splash-lang-toggle"
            aria-label={lang === "en" ? "भाषा बदलें" : "Change language"}
          >
            <Globe className="w-3.5 h-3.5" /> {t.langLabel}
          </button>
          <Link to="/jidnyasa" className="pill-jidnyasa"
                data-testid="splash-jidnyasa-link">
            <Sparkles className="w-3.5 h-3.5"/> JLP
          </Link>
          <Link to="/login" className="text-sm text-[hsl(var(--neu-ink))] hover:underline"
                data-testid="splash-signin-link">
            {t.signIn}
          </Link>
        </div>
      </header>

      {/* ─── Hero ─────────────────────────────────────────── */}
      <Section className="pt-10 sm:pt-16 pb-10">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center">
          {/* Left: copy */}
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4 rise-in">
              {t.hero.badge}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight rise-in">
              <span className="text-[hsl(var(--neu-ink))]">{t.hero.headline1}</span>
              <span className="block italic text-[hsl(var(--neu-orange))] mt-2">{t.hero.headline2}</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl rise-in">
              {t.hero.sub}
            </p>
            <p className="mt-3 italic text-[hsl(var(--neu-orange))] max-w-2xl leading-snug rise-in">
              {t.hero.subLine2}
            </p>

            {/* USP chips */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-8 rise-in">
              {t.usps.map((u, i) => {
                const Icon = u.icon;
                return (
                  <div key={i} className="card-soft p-3 sm:p-4 text-center">
                    <Icon className="w-5 h-5 mx-auto text-[hsl(var(--neu-orange))]" />
                    <div className="font-medium text-sm sm:text-base mt-2">{u.title}</div>
                    <div className="text-xs text-muted-foreground">{u.sub}</div>
                  </div>
                );
              })}
            </div>

            {/* Problem-anchor strip: bridges hero into the "Sound familiar?" section */}
            <div className="mt-6 rise-in" data-testid="splash-hero-problem-strip">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                {t.hero.problemStripLabel}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {t.hero.problemChips.map((c) => (
                  <span key={c}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white/70 border border-[hsl(var(--neu-orange))]/25 text-[hsl(var(--neu-ink))]">
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <Link to="/login"
                  className="btn-primary w-full sm:w-auto sm:min-w-[280px] text-lg mt-8 rise-in"
                  data-testid="splash-get-started-btn">
              {t.hero.cta} <ArrowRight className="w-4 h-4"/>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">{t.hero.ctaSub}</p>
          </div>

          {/* Right: warm Indian-family photo (order-first on mobile) */}
          <div className="rise-in order-first lg:order-last">
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-[#171531]/20 ring-1 ring-black/5">
              <img
                src="/hero-family.jpg"
                alt={t.hero.imageAlt}
                loading="eager"
                fetchPriority="high"
                width="1536" height="1024"
                className="w-full h-auto block"
              />
              {/* Subtle bottom fade so any card overlap reads cleanly */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
                   style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(23,21,49,0.15) 100%)" }} />
            </div>
          </div>
        </div>
      </Section>

      {/* ─── Two programs under NeuroEQ · simple, coherent ─────────────────── */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 pt-4 pb-14" data-testid="splash-programs">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
          NeuroEQ · two programs
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* NGP card */}
          <Link to="/login"
                 className="rounded-3xl p-6 bg-white border-2 border-[hsl(var(--neu-orange))]/25 hover:border-[hsl(var(--neu-orange))] hover:-translate-y-0.5 transition-all block relative overflow-hidden"
                 data-testid="splash-program-neuroeq">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-[hsl(var(--neu-orange))]"/>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--neu-orange))] inline-block px-2.5 py-1 rounded-full font-medium">
                NGP
              </span>
              <span className="text-xs text-muted-foreground">Parent-led · at home</span>
            </div>
            <div className="font-display text-xl mt-3 leading-tight">
              NeuroEQ Growth Program
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              A 30-minute Sunday ritual that builds self-discipline through parent-child bonding — your child gradually self-coaches using the material. <b>Ages 4–13.</b>
            </p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-[hsl(var(--neu-ink))]">
                ₹399<span className="text-xs text-muted-foreground font-normal">/mo</span>
              </span>
              <span className="btn-primary text-sm px-4 py-2">
                Start 2 free Sundays <ArrowRight className="w-3 h-3"/>
              </span>
            </div>
          </Link>
          {/* JJLP card */}
          <Link to="/jidnyasa"
                 className="rounded-3xl p-6 bg-white border-2 border-[hsl(var(--neu-green))]/25 hover:border-[hsl(var(--neu-green))] hover:-translate-y-0.5 transition-all block relative overflow-hidden"
                 data-testid="splash-program-jidnyasa">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-[hsl(var(--neu-green))]"/>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--neu-green))] inline-block px-2.5 py-1 rounded-full font-medium">
                JJLP
              </span>
              <span className="text-xs text-muted-foreground">Teacher-led · live cohort</span>
            </div>
            <div className="font-display text-xl mt-3 leading-tight">
              Jidnyasa Jr Leaders Program
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              A live 60-minute class every week with two parts in one session — <b>Knowledge of Self</b> + <b>Knowledge of World</b>. Builds young leaders. <b>Ages 7–13.</b>
            </p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-[hsl(var(--neu-ink))]">
                ₹499<span className="text-xs text-muted-foreground font-normal">/mo</span>
              </span>
              <span className="btn-jidnyasa text-sm px-4 py-2">
                Free trial · 1st Tue <ArrowRight className="w-3 h-3"/>
              </span>
            </div>
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center sm:text-left">
          Subscribe to either, or bundle both (Joint plan · save 20% yearly). Cancel each independently.
        </p>
      </section>

      {/* ─── Hook: Tired of reminding again and again? ───── */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-14" data-testid="splash-hook">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
          NGP · {t.hook.eyebrow}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-3">
          {t.hook.title}
        </h2>
        <p className="text-lg text-muted-foreground mt-4 leading-relaxed max-w-3xl">
          {t.hook.body}
        </p>
        <p className="mt-5 inline-block px-5 py-3 rounded-2xl bg-[hsl(var(--neu-ink))] text-white font-display text-lg">
          {t.hook.kicker}
        </p>
      </section>

      {/* ─── Three pillars: Connect / Practise / Grow (NGP) ─────── */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-10" data-testid="splash-pillars">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium mb-3">
          NGP · {t.pillars.eyebrow}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl">
          {t.pillars.title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
          {t.pillars.items.map((p, i) => {
            // Visual stepper: 1 Connect (heart) → 2 Practise (handshake) → 3 Grow (sunrise)
            const Icon = [Heart, Handshake, Sunrise][i];
            const bgTint = ["bg-[hsl(var(--neu-pink))]/10", "bg-[hsl(var(--neu-blue))]/10", "bg-[hsl(var(--neu-orange))]/10"][i];
            const iconColor = ["text-[hsl(var(--neu-pink))]", "text-[hsl(var(--neu-blue))]", "text-[hsl(var(--neu-orange))]"][i];
            return (
              <div key={p.pillar} className="card-elevated p-6 relative"
                    data-testid={`splash-pillar-${i + 1}`}>
                <div className={`w-12 h-12 rounded-2xl ${bgTint} inline-flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${iconColor}`} />
                </div>
                <div className="flex items-baseline gap-2 mt-4">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium">
                    {i + 1}
                  </span>
                  <span className="font-display text-xl">{p.pillar}</span>
                </div>
                <div className="text-sm font-medium mt-1 text-[hsl(var(--neu-ink))]">{p.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── Sound familiar? (Parent Problems) ─────────────────────────────────────────── */}
      <section className="relative py-16 sm:py-24 my-10 overflow-hidden"
                style={{ background: "linear-gradient(180deg, #171531 0%, #232048 100%)" }}
                data-testid="splash-problems-section">
        {/* Subtle mandala rings decoration */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full border border-[#FF7A3D]/10" />
        <div className="pointer-events-none absolute -top-16 -right-16 w-80 h-80 rounded-full border border-[#FF7A3D]/10" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full border border-[#FFD27A]/10" />
        <div className="max-w-5xl mx-auto px-6 sm:px-8 relative">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD27A] font-medium mb-3">
            NGP · {t.problems.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight text-white max-w-3xl">
            {t.problems.title}
          </h2>
          <p className="text-base sm:text-lg mt-4 max-w-2xl leading-relaxed"
             style={{ color: "rgba(255,255,255,0.72)" }}>
            {t.problems.sub}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {t.problems.items.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i}
                      className="group rounded-[22px] p-5 transition-all duration-300 hover:-translate-y-0.5"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                      data-testid={`splash-problem-card-${i}`}>
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
                       style={{ background: "rgba(249,115,22,0.15)" }}>
                    <Icon className="w-5 h-5" style={{ color: "#FFB27A" }} />
                  </div>
                  <div className="font-display text-xl text-white">{p.title}</div>
                  <p className="mt-2 italic text-sm leading-relaxed"
                     style={{ color: "#FFD27A" }}>
                    {p.moment}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed"
                     style={{ color: "rgba(255,255,255,0.72)" }}>
                    {p.fix}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── The Sunday ritual (How it works) ─────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 my-6" style={{ background: "#FBF4E9" }}
                data-testid="splash-how-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
               style={{ color: "#FF7A3D" }}>
            NGP · {t.how.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl"
              style={{ color: "#20233A" }}>
            {t.how.title}
          </h2>
          <p className="text-base sm:text-lg mt-4 max-w-2xl leading-relaxed"
             style={{ color: "rgba(32,35,58,0.7)" }}>
            {t.how.sub}
          </p>
          {/* Desktop: dotted connector line running behind the badges */}
          <div className="relative mt-12">
            <div className="hidden lg:block absolute top-10 left-[8%] right-[8%] z-0 pointer-events-none"
                 aria-hidden="true"
                 style={{
                   borderTop: "2px dashed rgba(249,115,22,0.35)",
                   height: 1,
                 }} />
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 z-10">
              {t.how.steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="flex flex-col items-center text-center"
                        data-testid={`splash-how-step-${i + 1}`}>
                    {/* Circular badge with a subtle flame/lamp motif behind */}
                    <div className="relative w-20 h-20 rounded-full bg-white shadow-lg shadow-[#20233A]/10 flex items-center justify-center ring-1 ring-black/5">
                      <span aria-hidden className="absolute -top-1.5 w-2 h-2 rounded-full"
                             style={{ background: "#FF7A3D", boxShadow: "0 0 12px #FF7A3D" }} />
                      <Icon className="w-8 h-8" style={{ color: "#FF7A3D" }} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest mt-4 font-medium"
                         style={{ color: "#FF7A3D" }}>
                      {t.how.stepLabel} {i + 1}
                    </div>
                    <div className="font-display text-xl mt-1" style={{ color: "#20233A" }}>
                      {s.title}
                    </div>
                    <p className="text-sm mt-2 leading-relaxed max-w-[240px]"
                       style={{ color: "rgba(32,35,58,0.72)" }}>
                      {s.sub}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Outcomes ─────────────────────────────────────────── */}
      <Section className="py-14 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl leading-tight">{t.outcomes.title}</h2>
        <p className="text-muted-foreground mt-3 max-w-2xl">{t.outcomes.sub}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
          {t.outcomes.items.map((o, i) => {
            const Icon = o.icon;
            return (
              <div key={i} className="card-soft p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--neu-green))]/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[hsl(var(--neu-green))]" />
                </div>
                <div className="font-medium text-sm sm:text-base">{o.label}</div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ─── Unique differentiator ─────────────────────────────────────────── */}
      <Section className="py-14 sm:py-20">
        <div className="card-elevated relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--neu-orange))]/5 via-transparent to-[hsl(var(--neu-pink))]/5" />
          <div className="relative p-8 sm:p-10">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              {t.unique.badge}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-3">
              {t.unique.title}
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              {t.unique.body}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {t.unique.chips.map((c) => <Chip key={c}>{c}</Chip>)}
            </div>
          </div>
        </div>
      </Section>

      {/* ─── Final CTA ─────────────────────────────────────────── */}
      <Section className="py-16 text-center">
        <Wordmark className="text-2xl block mb-3" />
        <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-xl mx-auto">
          {t.finalCta.title}
        </h2>
        <Link to="/login"
              className="btn-primary min-w-[260px] text-lg mt-8 mx-auto"
              data-testid="splash-get-started-btn-2">
          {t.finalCta.cta} <ArrowRight className="w-4 h-4"/>
        </Link>
        <p className="text-xs text-muted-foreground mt-3">{t.finalCta.note}</p>

        <div className="mt-16 text-xs text-muted-foreground/70">{t.footer}</div>
      </Section>
    </div>
  );
}
